import React, { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash.get';

import { Div, Icon } from '@pinpoint/design-kit';

import { segmentsViewActions, bcActions } from 'src/store/actions';
import { OceanWrapper, SegmentViewWrapper, Button } from 'src/components';
import { GraphicPage } from 'src/containers';
import CONSTANTS from 'src/store/constants';
import { ROUTES } from 'src/config';
import { themeFromTag } from 'src/functions';

const FACTORS = 5;
const FACETS = 30;

const EXPLORER = 'factor-explorer';

const FINGERPRINT = 'fingerprint';

function SegmentsView({
  history,
  location: { search: queryParams, pathname },
  match: {
    params: { entity: entityParam, entityId: entityIdParam },
  },
  app: { tenant, client },
  segmentsView: { entity, fetchEntityStatus },
  fetchEntity,
  setDatasetId,
  setSegmentId,
  setDatasetName,
  setSegmentName,
  editEntity,
}) {
  const [variant, setVariant] = useState(FINGERPRINT);
  const [hasData, setHasData] = useState(false);

  const data = useMemo(() => {
    let nonFactor = {};
    if (entity.id) {
      const { dataset, name, filter, pxScores, pxHistogram, ...rest } = entity;
      nonFactor = {
        ...rest,
        dataset,
        filter,
        name: filter === 'POPULATION_SNAPSHOT' ? dataset.name : name,
        pxScores: pxScores.filter(({ trait: { subType } }) => subType !== 'factor'),
      };
    }
    return {
      nonFactor,
      entity,
    };
  }, [entity]);

  const getData = useCallback(() => {
    fetchEntity(entityParam, entityIdParam);
  }, [fetchEntity, entityIdParam, entityParam]);
  const loaded = hasData && entity.pxScores && entity.pxScores.length >= FACTORS + FACETS;
  /**
   * Once data is loaded, set breadcrumb details
   * TODO: Check if this is still needed or
   * can it be removed
   */
  useEffect(() => {
    if (entity && entity.pxScores) {
      if (entity.dataset) {
        setDatasetId(entity.dataset.id);
        setDatasetName(entity.dataset.name);
      }
      if (entity.filter !== 'POPULATION_SNAPSHOT') {
        setSegmentId(entity.id);
        setSegmentName(entity.name);
      } else {
        setSegmentId(undefined);
        setSegmentName(undefined);
      }
    }
  }, [entity, setDatasetId, setDatasetName, setSegmentId, setSegmentName]);

  useEffect(() => {
    if (fetchEntityStatus === CONSTANTS.API.FAILED) setHasData(false);
    if (fetchEntityStatus === CONSTANTS.API.SUCCEEDED) setHasData(true);
  }, [fetchEntityStatus]);

  /*
   * Fetch data whenever the tenant or params change
   */
  useEffect(() => {
    if (tenant) {
      getData();
    }
  }, [tenant, getData]);

  // When there's new query params parse for variant, redirect to FINGERPRINT if not explicitly set
  useEffect(() => {
    const query = new URLSearchParams(queryParams);
    const nextChartType = query.get('variant');
    if (!nextChartType) {
      query.append('variant', FINGERPRINT);
      history.replace(`${pathname}?${query.toString()}`);
    } else {
      setVariant(nextChartType);
    }
  }, [queryParams, history, pathname]);

  return (
    <>
      <Div d="flex" justify="space-between" align="center" w="100%" p={{ x: '1rem' }} m={{ t: '1rem', b: '1.25rem' }}>
        <Div d="flex" justify="flex-start" align="center">
          <Button
            buttonStyle="primary"
            onClick={() => {
              history.push(ROUTES.SEGMENTS);
            }}
          >
            <Icon name="LongLeft" color="white" m={{ r: '0.5rem' }} size="1rem" />
            BACK
          </Button>
          <Button
            buttonStyle="tab"
            selected={variant === FINGERPRINT}
            data-tip="View main analytics dashboard"
            onClick={() => {
              history.push(`${pathname}?variant=${FINGERPRINT}`);
            }}
          >
            <Icon name="Visualize" color="white" m={{ r: '0.5rem' }} size="1rem" />
            VISUALIZE
          </Button>
          <Button
            buttonStyle="tab"
            selected={variant === EXPLORER}
            data-tip="See factors and their facets"
            onClick={() => {
              history.push(`${pathname}?variant=${EXPLORER}`);
            }}
          >
            <Icon name="Fingerprint" color="white" m={{ r: '1em' }} size="1rem" />
            FACTOR EXPLORER
          </Button>
        </Div>
        {get(entity, 'clusters', []).length > 0 && client.canPersonalize && get(entity, 'tag') === 'HIT' && (
          <Div d="flex" justify="flex-end" align="center">
            <Button
              buttonStyle="primary"
              m={{ r: '0rem' }}
              data-tip="View subsets within this dataset"
              onClick={() => {
                history.push(ROUTES.CLUSTERS.replace(':segmentId', entity.id));
              }}
            >
              <Icon name="Clusters" color="white" m={{ r: '0.625rem' }} size="1rem" />
              CLUSTERS
            </Button>
          </Div>
        )}
      </Div>
      <Div h="calc(100% - 3.75rem)" m={{ x: '.9375rem' }}>
        {variant === FINGERPRINT ? (
          <SegmentViewWrapper
            getData={getData}
            hasData={loaded}
            data={data.nonFactor}
            apiStatus={fetchEntityStatus}
            onThemeChange={(id, theme) => {
              editEntity(entityParam, id, { theme });
            }}
            theme={data.nonFactor.theme}
            defaultTheme={themeFromTag(data.nonFactor)}
          />
        ) : (
          <OceanWrapper getData={getData} hasData={hasData} data={data.entity} apiStatus={fetchEntityStatus} />
        )}
      </Div>
    </>
  );
}

const mapStateToProps = ({ app, bc, segmentsView }) => ({
  app,
  bc,
  segmentsView,
});

const mapDispatchToProps = (dispatch) => ({
  setDatasetId: (datasetId) => dispatch(bcActions.setDatasetId(datasetId)),
  setDatasetName: (datasetName) => dispatch(bcActions.setDatasetName(datasetName)),
  setSegmentId: (entityId) => dispatch(bcActions.setSegmentId(entityId)),
  setSegmentName: (segmentName) => dispatch(bcActions.setSegmentName(segmentName)),
  fetchEntity: (...params) => dispatch(segmentsViewActions.fetchEntity(...params)),
  editEntity: (...params) => dispatch(segmentsViewActions.editEntity(...params)),
});

SegmentsView.propTypes = {
  app: PropTypes.shape({
    tenant: PropTypes.string.isRequired,
    client: PropTypes.shape({
      canPersonalize: PropTypes.bool,
    }),
  }).isRequired,
  segmentsView: PropTypes.shape({
    entity: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      pxScores: PropTypes.arrayOf(PropTypes.shape({})),
      tag: PropTypes.string,
      dataset: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
      filter: PropTypes.string,
      pxHistogram: PropTypes.arrayOf(PropTypes.shape({})),
      clusters: PropTypes.arrayOf(PropTypes.shape({})),
    }).isRequired,
    fetchEntityStatus: PropTypes.oneOf([
      CONSTANTS.API.STARTED,
      CONSTANTS.API.SUCCEEDED,
      CONSTANTS.API.FAILED,
      undefined,
    ]),
  }).isRequired,
  fetchEntity: PropTypes.func.isRequired,
  setDatasetId: PropTypes.func.isRequired,
  setDatasetName: PropTypes.func.isRequired,
  setSegmentId: PropTypes.func.isRequired,
  setSegmentName: PropTypes.func.isRequired,
  editEntity: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(GraphicPage(SegmentsView));
